import React, {useMemo, useState} from 'react';
import {DocumentReadWReferences, AssociationReadWReferenceLoad} from '../../../../schemas';
import {ExchangeType, ExchangeTypeLabels} from '../../../../routines/schemas/transcript_analysis';
import {ReferenceReadWText, formatCitation} from '../../../../schemas/reference';
import {useTagContext} from '../../../../contexts/tag_context';
import {useDocumentSpotlight} from '../../../../contexts/document_spotlight_context';
import {SlArrowDown, SlArrowRight} from 'react-icons/sl';
import {FiNavigation, FiEye, FiEyeOff, FiCopy} from 'react-icons/fi';

// ── Exchange type styling ─────────────────────────────────────────────────

const exchangeTypeStyles: Record<ExchangeType, { bg: string; border: string; text: string; icon: string }> = {
    [ExchangeType.ADMISSION]: {bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', icon: '\u2713'},
    [ExchangeType.DENIAL]: {bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-800', icon: '\u2717'},
    [ExchangeType.INCONSISTENCY]: {bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', icon: '\u26A0'},
    [ExchangeType.KEY_FACT]: {bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', icon: '\u2605'},
    [ExchangeType.OBJECTION]: {bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', icon: '\u2716'},
    [ExchangeType.CREDIBILITY]: {bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-800', icon: '\u25C6'},
    [ExchangeType.PROCEDURAL]: {bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-600', icon: '\u2014'},
};

// ── Types ─────────────────────────────────────────────────────────────────

interface GroupedExchange {
    exchangeType: ExchangeType;
    references: ReferenceReadWText[];
    tagName?: string;
}

// ── Exchange card ─────────────────────────────────────────────────────────

const ExchangeCard: React.FC<{
    ref_: ReferenceReadWText;
    style: typeof exchangeTypeStyles[ExchangeType.ADMISSION];
    onJump?: (text: string, source: string, pageHint?: number) => void;
    onToggleHighlight?: (text: string, source: string, pageHint?: number) => void;
    isHighlighted?: boolean;
}> = ({ref_, style, onJump, onToggleHighlight, isHighlighted}) => {
    // Build a specific source label from the reference (exchange type + speaker)
    const label = ref_.exchange_type ? ExchangeTypeLabels[ref_.exchange_type as ExchangeType] : 'Reference';
    // Speaker is in the explanation, typically "Speaker Name: significance..."
    const speaker = ref_.explanation?.split(':')[0]?.trim();
    const source = speaker ? `${label}: ${speaker}` : label;
    const pageHint = ref_.page_start ?? undefined;

    return (
    <div className={`rounded-lg border ${style.border} ${style.bg} p-3 transition-all hover:shadow-sm`}>
        {ref_.referenced_text && (
            <blockquote className="text-sm text-slate-800 italic leading-relaxed border-l-2 border-slate-300 pl-3 mb-2">
                &ldquo;{ref_.referenced_text}&rdquo;
            </blockquote>
        )}
        <div className="flex items-center gap-1">
            {ref_.explanation && (
                <p className="text-xs text-slate-600 leading-relaxed flex-1">
                    {ref_.page_start != null && (
                        <span className="text-slate-400 mr-1">{formatCitation(ref_)}</span>
                    )}
                    {ref_.explanation}
                </p>
            )}
            {ref_.referenced_text && (
                <div className="flex items-center gap-0.5 shrink-0 ml-2">
                    <button
                        type="button"
                        onClick={() => onJump?.(ref_.referenced_text, source, pageHint)}
                        className="p-1 rounded hover:bg-black/5 text-slate-400 hover:text-slate-700 transition-colors"
                        title="Jump to quote in document"
                    >
                        <FiNavigation className="w-3.5 h-3.5"/>
                    </button>
                    <button
                        type="button"
                        onClick={() => onToggleHighlight?.(ref_.referenced_text, source, pageHint)}
                        className={`p-1 rounded transition-colors ${
                            isHighlighted
                                ? 'text-amber-600 hover:bg-amber-50'
                                : 'text-slate-400 hover:text-slate-700 hover:bg-black/5'
                        }`}
                        title={isHighlighted ? 'Hide highlight' : 'Show highlight'}
                    >
                        {isHighlighted
                            ? <FiEye className="w-3.5 h-3.5"/>
                            : <FiEyeOff className="w-3.5 h-3.5"/>
                        }
                    </button>
                    {ref_.page_start != null && (
                        <button
                            type="button"
                            onClick={() => {
                                const cite = formatCitation(ref_);
                                navigator.clipboard.writeText(cite);
                            }}
                            className="p-1 rounded hover:bg-black/5 text-slate-400 hover:text-slate-700 transition-colors"
                            title={`Copy citation: ${formatCitation(ref_)}`}
                        >
                            <FiCopy className="w-3.5 h-3.5"/>
                        </button>
                    )}
                </div>
            )}
        </div>
    </div>
    );
};

// ── Exchange type section ─────────────────────────────────────────────────

const ExchangeTypeSection: React.FC<{
    group: GroupedExchange;
    defaultOpen?: boolean;
    onJump?: (text: string, source: string, pageHint?: number) => void;
    onToggleHighlight?: (text: string, source: string, pageHint?: number) => void;
    isHighlighted?: (text: string) => boolean;
}> = ({group, defaultOpen = true, onJump, onToggleHighlight, isHighlighted}) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const style = exchangeTypeStyles[group.exchangeType];
    const label = ExchangeTypeLabels[group.exchangeType];

    return (
        <div className="mb-4">
            <button
                type="button"
                onClick={() => setIsOpen(o => !o)}
                className={`flex items-center gap-2 w-full text-left px-3 py-2 rounded-lg ${style.bg} ${style.border} border transition-colors hover:shadow-sm`}
            >
                <span className="text-base leading-none">{style.icon}</span>
                <span className={`text-sm font-semibold ${style.text}`}>
                    {label}
                </span>
                <span className={`text-xs ${style.text} opacity-60`}>
                    ({group.references.length})
                </span>
                {group.tagName && (
                    <span className="ml-auto text-[10px] text-slate-400 font-normal">
                        {group.tagName}
                    </span>
                )}
                <span className={`ml-auto text-xs ${style.text} opacity-50`}>
                    {isOpen ? '\u25B2' : '\u25BC'}
                </span>
            </button>

            {isOpen && (
                <div className="mt-2 space-y-2 pl-2">
                    {group.references.map((ref_, i) => (
                        <ExchangeCard
                            key={ref_.id || i}
                            ref_={ref_}
                            style={style}
                            onJump={onJump}
                            onToggleHighlight={onToggleHighlight}
                            isHighlighted={isHighlighted?.(ref_.referenced_text) ?? false}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

// ── Main component ────────────────────────────────────────────────────────

const TranscriptAnalysisView: React.FC<{
    doc: DocumentReadWReferences;
    /** If provided, the palette's open state is controlled externally. */
    isOpen?: boolean;
    onToggle?: () => void;
}> = ({doc, isOpen: controlledOpen, onToggle}) => {
    const tagContext = useTagContext();
    const tagMap = tagContext?.tagMap;
    const spotlightCtx = useDocumentSpotlight();
    const [internalOpen, setInternalOpen] = useState(true);
    const isControlled = controlledOpen !== undefined;
    const isPaletteOpen = isControlled ? controlledOpen : internalOpen;
    const togglePalette = () => {
        if (isControlled) onToggle?.();
        else setInternalOpen(cur => !cur);
    };

    const handleJump = (text: string, source: string, pageHint?: number) => {
        spotlightCtx?.jumpToSpotlight({text, source, pageHint});
    };

    const handleToggleHighlight = (text: string, source: string, pageHint?: number) => {
        spotlightCtx?.toggleOrAddSpotlight({text, source, pageHint});
    };

    const isHighlighted = (text: string): boolean => {
        return !!spotlightCtx?.findByText(text);
    };

    const grouped = useMemo(() => {
        const groups = new Map<ExchangeType, GroupedExchange>();

        for (const assoc of (doc.applied_tag_associations ?? []) as AssociationReadWReferenceLoad[]) {
            const refs = (assoc.references ?? []) as ReferenceReadWText[];
            const tag = tagMap?.get(assoc.tag_id);

            for (const ref_ of refs) {
                if (!ref_.exchange_type) continue;
                const et = ref_.exchange_type as ExchangeType;

                if (!groups.has(et)) {
                    groups.set(et, {
                        exchangeType: et,
                        references: [],
                        tagName: tag?.tag_name,
                    });
                }
                groups.get(et)!.references.push(ref_);
            }
        }

        // Sort references within each group by page number (document order)
        for (const group of groups.values()) {
            group.references.sort((a, b) => (a.page_start ?? 0) - (b.page_start ?? 0));
        }

        const priority: ExchangeType[] = [
            ExchangeType.ADMISSION,
            ExchangeType.INCONSISTENCY,
            ExchangeType.KEY_FACT,
            ExchangeType.DENIAL,
            ExchangeType.CREDIBILITY,
            ExchangeType.OBJECTION,
            ExchangeType.PROCEDURAL,
        ];

        return priority
            .filter(et => groups.has(et))
            .map(et => groups.get(et)!);
    }, [doc.applied_tag_associations, tagMap]);

    if (grouped.length === 0) return null;

    const totalExchanges = grouped.reduce((sum, g) => sum + g.references.length, 0);

    return (
        <div className="rounded-lg border border-slate-300 bg-white shadow-sm w-full">
            {/* Header toggle */}
            <button
                type="button"
                onClick={togglePalette}
                className="flex w-full items-center justify-between px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
                <span className="flex items-center gap-2">
                    {isPaletteOpen
                        ? <SlArrowDown className="m-1 rounded text-slate-600 hover:text-slate-900"/>
                        : <SlArrowRight className="m-1 rounded text-slate-600 hover:text-slate-900"/>
                    }
                    <span>Key Testimony</span>
                </span>
                <span className="text-xs text-slate-400">
                    {totalExchanges} exchange{totalExchanges !== 1 ? 's' : ''} across {grouped.length} categor{grouped.length !== 1 ? 'ies' : 'y'}
                </span>
            </button>

            {/* Collapsible body */}
            <div
                className={`overflow-hidden transition-[max-height,opacity] duration-300 ${
                    isPaletteOpen ? 'max-h-none opacity-100' : 'max-h-0 opacity-0'
                }`}
            >
                <div className="px-3 pt-3 pb-5">
                    {grouped.map(group => (
                        <ExchangeTypeSection
                            key={group.exchangeType}
                            group={group}
                            defaultOpen={group.exchangeType !== ExchangeType.PROCEDURAL}
                            onJump={handleJump}
                            onToggleHighlight={handleToggleHighlight}
                            isHighlighted={isHighlighted}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default TranscriptAnalysisView;
